import React from "react";
import { Link } from "react-router-dom";
import {
  Moon, ArrowRight, Home, Car, Users, HeartPulse, ClipboardList, Dumbbell,
  UserCircle2, ShieldCheck, Sparkles, Heart, Star, Phone, ChevronRight,
} from "lucide-react";
import StarField from "../components/StarField";
import { COMPANY, SERVICES, VALUES, TESTIMONIALS, STATS } from "../mock/mockData";

const ICONS = { Home, Car, Users, HeartPulse, ClipboardList, Dumbbell, UserCircle2, ShieldCheck, Sparkles, Heart };

const HomePage = () => {
  return (
    <div className="relative" data-testid="home-page">
      {/* ========== HERO ========== */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden" data-testid="hero-section">
        <StarField count={80} />
        {/* Radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_40%,rgba(212,165,116,0.12),transparent_50%)] pointer-events-none" />
        <div className="absolute -left-40 top-1/2 -translate-y-1/2 w-[600px] h-[600px] pointer-events-none opacity-50">
          <div className="gold-arc w-full h-full" />
        </div>

        <div className="container-lux grid lg:grid-cols-12 gap-16 items-center relative z-10 py-32">
          <div className="lg:col-span-7 fade-up">
            <div className="divider-gold mb-8" data-testid="hero-eyebrow">
              <span>Perth & Surrounding Areas</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white mb-8">
              Support that
              <br />
              goes <span className="italic text-gold">further.</span>
            </h1>
            <p className="text-lg text-white/70 max-w-xl leading-relaxed mb-10 fade-up-delay-1">
              Compassionate. Reliable. Personalised. We empower independence and enhance quality of life through support services tailored around you - not the other way around.
            </p>
            <div className="flex flex-wrap items-center gap-4 fade-up-delay-2">
              <Link
                to="/book"
                className="btn-gold inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-semibold tracking-[0.12em] uppercase text-deep hover:bg-gold-light transition-all hover:scale-[1.03]"
                data-testid="hero-book-btn"
              >
                <Moon className="w-4 h-4" /> Book an Appointment
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-sm font-medium tracking-[0.12em] uppercase text-white hover:text-gold transition-colors link-gold"
                data-testid="hero-services-link"
              >
                Our Services <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-8 mt-20 pt-10 border-t border-white/10 fade-up-delay-3 max-w-md" data-testid="hero-stats">
              {STATS.map((s) => (
                <div key={s.label}>
                  <div className="font-serif text-3xl md:text-4xl text-gold">{s.value}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/50 mt-2">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Moon logo visual */}
          <div className="lg:col-span-5 relative flex items-center justify-center fade-up-delay-2">
            <div className="relative w-[340px] h-[340px] md:w-[440px] md:h-[440px]">
              {/* Orbit rings */}
              <div className="absolute inset-0 rounded-full border border-gold/15" />
              <div className="absolute inset-6 rounded-full border border-gold/10" />
              <div className="absolute inset-14 rounded-full border border-gold/5" />
              <div className="absolute inset-0 gold-arc" />

              {/* Center logo */}
              <div className="absolute inset-16 rounded-full bg-deep flex items-center justify-center float-gentle glow-pulse">
                <img src={COMPANY.logo} alt="Moon and Back Support" className="w-full h-full object-cover rounded-full moon-glow" />
              </div>

              {/* Tiny orbiting stars */}
              <div className="absolute top-4 left-1/2 w-2 h-2 rounded-full bg-gold animate-pulse" />
              <div className="absolute bottom-10 right-6 w-1.5 h-1.5 rounded-full bg-white/80" />
              <div className="absolute top-20 right-0 w-1 h-1 rounded-full bg-gold-light" />
            </div>
          </div>
        </div>
      </section>

      {/* ========== MISSION ========== */}
      <section className="relative py-28 md:py-40 overflow-hidden" data-testid="mission-section">
        <div className="container-lux grid lg:grid-cols-2 gap-20 items-center">
          <div>
            <div className="divider-gold mb-6">Your Goals. Our Mission.</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-8">
              We're here to make <em className="text-gold not-italic font-normal">everyday life</em> easier, more connected, and more fulfilling.
            </h2>
            <p className="text-white/70 leading-relaxed mb-6">
              At Moon and Back Support, we don't believe in one-size-fits-all care. We provide person-centred support that's flexible, respectful, and built around what matters most to <em className="text-gold not-italic">you</em>.
            </p>
            <p className="text-white/70 leading-relaxed mb-10">
              From a quiet cup of tea to a day at the community centre - every support moment is an opportunity for someone to feel seen, heard, and truly cared for.
            </p>
            <Link to="/about" className="inline-flex items-center gap-3 text-sm tracking-[0.12em] uppercase text-gold link-gold" data-testid="mission-about-link">
              Discover Our Story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Values grid */}
          <div className="grid sm:grid-cols-2 gap-5">
            {VALUES.map((v, i) => {
              const Icon = ICONS[v.icon];
              return (
                <div key={v.title} className={`lux-card rounded-2xl p-7 fade-up-delay-${i + 1}`} data-testid={`value-${i}`}>
                  <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/20 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="font-serif text-xl mb-2 text-white">{v.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{v.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== SERVICES BENTO ========== */}
      <section className="relative py-28 md:py-40 bg-gradient-to-b from-transparent via-[#0A0A12] to-transparent" data-testid="services-preview">
        <div className="container-lux">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="divider-gold mb-6">Our Services</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-6">
              A wide range of support, <br />
              <em className="text-gold not-italic">tailored to your life.</em>
            </h2>
            <p className="text-white/70 leading-relaxed">
              Six core categories. Endless combinations. All designed to help you live independently and comfortably.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon];
              return (
                <Link
                  key={s.id}
                  to="/services"
                  className="lux-card group rounded-3xl overflow-hidden flex flex-col"
                  data-testid={`service-card-${s.id}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-deep via-deep/40 to-transparent" />
                    <div className="absolute top-4 left-4 w-12 h-12 rounded-full bg-deep/80 backdrop-blur-md border border-gold/30 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gold" />
                    </div>
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="font-serif text-2xl text-white mb-2">{s.title}</h3>
                    <p className="text-sm text-white/60 mb-6 leading-relaxed">{s.tagline}</p>
                    <div className="mt-auto flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-gold">
                      Learn more <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section className="relative py-28 md:py-40 overflow-hidden" data-testid="testimonials-section">
        <StarField count={40} />
        <div className="container-lux relative">
          <div className="text-center mb-20">
            <div className="divider-gold mb-6">Kind Words</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Stories from the people <br />
              we're <em className="text-gold not-italic">honoured to support.</em>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {TESTIMONIALS.slice(0, 4).map((t, i) => (
              <div key={t.id} className={`lux-card rounded-3xl p-10 relative fade-up-delay-${(i % 4) + 1}`} data-testid={`testimonial-${t.id}`}>
                <div className="absolute top-6 right-6 font-serif text-6xl text-gold/20 leading-none">"</div>
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: t.rating }).map((_, idx) => (
                    <Star key={idx} className="w-4 h-4 fill-gold text-gold" />
                  ))}
                </div>
                <p className="text-white/80 leading-relaxed font-serif text-lg mb-8">
                  {t.quote}
                </p>
                <div className="flex items-center gap-4 pt-5 border-t border-white/10">
                  <div className="w-11 h-11 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold font-serif text-lg">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm text-white">{t.name}</div>
                    <div className="text-xs text-white/50">{t.relation}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CTA ========== */}
      <section className="relative py-28 md:py-36 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,165,116,0.15),transparent_60%)]" />
        <div className="container-lux relative">
          <div className="lux-card rounded-[2rem] p-12 md:p-20 text-center max-w-4xl mx-auto relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 pointer-events-none opacity-40">
              <div className="gold-arc w-full h-full" />
            </div>
            <Moon className="w-12 h-12 text-gold mx-auto mb-8 moon-glow" />
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
              Let's make <em className="text-gold not-italic">each day</em> <br />
              your best day.
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
              We're here for you, every step of the way. Reach out for a friendly conversation - no pressure, no commitment.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link to="/book" className="btn-gold inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold tracking-[0.12em] uppercase text-deep hover:bg-gold-light transition-all hover:scale-[1.03]" data-testid="cta-book-btn">
                <Moon className="w-4 h-4" /> Book a Consultation
              </Link>
              <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-3 text-sm tracking-[0.12em] uppercase text-white hover:text-gold link-gold" data-testid="cta-phone-btn">
                <Phone className="w-4 h-4" /> {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
